import { AlertCircleIcon } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import {
  JobForm,
  type JobFormSubmitValues,
} from '@/modules/jobs/components/jobForm/JobForm';
import { useCreateJobMutation } from '@/modules/jobs/mutations/useCreateJobMutation';
import { useJobQuery } from '@/modules/jobs/queries/useJobQuery';
import {
  emphasisForRequest,
  savedEmphasis,
} from '@/modules/jobs/signal/emphasis';
import { countriesForRequest } from '@/modules/jobs/signal/location';
import { Alert, AlertDescription } from '@/modules/ui/components/alert/Alert';
import Container from '@/modules/ui/components/container/Container';
import Loader from '@/modules/ui/components/loader/Loader';
import { PageHeader } from '@/modules/ui/components/page-header/PageHeader';
import { extractErrorMessage } from '@/utils/extractErrorMessage';

const DuplicateJob = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const jobQuery = useJobQuery(id ?? '', {
    enabled: !!id,
  });
  const addJobMutation = useCreateJobMutation();

  const handleSubmit = (values: JobFormSubmitValues) => {
    const original = jobQuery.data;
    // The form has no emphasis either: the copy keeps the original's, on the skills it still has.
    const skillEmphasis = original
      ? emphasisForRequest({
          mustHave: values.mustHaveSkills ?? [],
          niceToHave: values.niceToHaveSkills ?? [],
          skillEmphasis: savedEmphasis(original.skillEmphasis),
        })
      : {};
    addJobMutation.mutate(
      {
        ...values,
        mustHaveSkills: values.mustHaveSkills ?? [],
        niceToHaveSkills: values.niceToHaveSkills ?? [],
        userIds: values.userIds ?? [],
        // The form has no countries: the copy searches where the original does, and its search starts on creation.
        ...(original &&
          countriesForRequest(
            original.countries,
            original.isAnywhere,
            values.locationTypes ?? [],
          )),
        ...(Object.keys(skillEmphasis).length > 0 && { skillEmphasis }),
        // Nor a company size band: the copy prefers the original's.
        ...(original &&
          original.employerSize !== 'Any' && {
            employerSize: original.employerSize,
          }),
        // Nor industries: the copy prefers the original's too.
        ...(original &&
          original.employerIndustries.length > 0 && {
            employerIndustries: original.employerIndustries,
          }),
        // And its extra search titles.
        ...(original &&
          original.extraSearchTitles.length > 0 && {
            extraSearchTitles: original.extraSearchTitles,
          }),
      },
      {
        onSuccess: () => {
          toast.success('Job has been duplicated');
          navigate('/jobs');
        },
        onError: (error) => {
          toast.error(
            `Job has not been duplicated: ${extractErrorMessage(error)}`,
          );
        },
      },
    );
  };

  return (
    <Container>
      <PageHeader title="Duplicate Job" />

      {jobQuery.isLoading && <Loader />}

      {jobQuery.isError && (
        <Alert variant="destructive">
          <AlertCircleIcon />
          <AlertDescription>Job not found</AlertDescription>
        </Alert>
      )}

      {jobQuery.isSuccess && (
        <JobForm
          defaultValues={jobQuery.data}
          mode="create"
          submitText="Add Job"
          loading={addJobMutation.isPending}
          onSubmit={handleSubmit}
          onCancel={() => navigate('/jobs')}
        />
      )}
    </Container>
  );
};

export default DuplicateJob;
